import React from 'react';
import { classInk } from './classIdentity';

// Shared look for the teacher pages: one accent (Deskey green, darkened to
// read on white), slate neutrals, hairline cards and one status-dot language.

export const ACCENT = '#34A853'; // dots, focus rings, toggles — never text on white
export const PRIMARY_BTN = 'h-10 px-4 rounded-lg shadow-none bg-[#1A7F37] hover:bg-[#166B2E] text-white text-sm font-medium transition-[background-color,transform] active:scale-[0.98] disabled:opacity-50';
export const SECONDARY_BTN = 'h-10 px-4 rounded-lg shadow-none border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 text-sm font-medium transition-colors disabled:opacity-50';
const CARD_FLAT = 'bg-white rounded-xl border border-slate-200';
// Cards sit just off the slate page; clickable ones warm to green on hover.
export const CARD = `${CARD_FLAT} shadow-[0_1px_2px_rgba(15,23,42,0.04)]`;
export const CARD_HOVER = 'transition-[border-color,box-shadow] hover:border-[#9ED3AC] hover:shadow-[0_4px_16px_-6px_rgba(52,168,83,0.35)]';
const HEADING_FONT = { fontFamily: 'Manrope, sans-serif' };
// Deskey navy, the sidebar's colour: the short rule under page and section titles.
const RULE_COLOR = '#1A365D';
export const INPUT = 'w-full h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#34A853]/30 focus:border-[#34A853]';
export const FIELD_LABEL = 'block text-sm font-medium text-slate-700 mb-1.5';
// A choosable card or row: green hairline and tint when picked, quiet otherwise.
export const choiceClass = (active) => `rounded-lg border text-left transition-colors ${active ? 'border-[#1A7F37] bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`;

// Where a session or lesson stands. The same five tones everywhere.
export const STATUS_TONES = {
  neutral: '#94a3b8', // synced, queued, skipped
  working: '#d97706', // transcribing, processing
  todo: '#2563eb', // ready for the teacher: review, edit, draft
  done: ACCENT, // published
  error: '#dc2626',
};

/** `● Draft` — the teacher side's one status mark. */
export function StatusDot({ tone = 'neutral', label, className = 'text-sm text-slate-600' }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: STATUS_TONES[tone] || STATUS_TONES.neutral }} aria-hidden="true" />
      {label}
    </span>
  );
}

// Darker shade of each tone so the label reads at 4.5:1 on its own 12% tint.
const TONE_INK = {
  neutral: '#475569',
  working: '#92400e',
  todo: '#1e40af',
  done: '#1A7F37',
  error: '#b91c1c',
};

/** A filled status pill: tone at 12% behind, a darker shade for the text. */
export function StatusBadge({ tone = 'neutral', label }) {
  const base = STATUS_TONES[tone] || STATUS_TONES.neutral;
  return (
    <span
      className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-md text-xs font-medium whitespace-nowrap flex-shrink-0"
      style={{ background: `${base}1f`, color: TONE_INK[tone] || TONE_INK.neutral }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: base }} aria-hidden="true" />
      {label}
    </span>
  );
}

/** One count on a page header: a tinted icon tile in the status tone, the label and the figure. */
export function CountTile({ label, val, tone = 'neutral', icon: Icon }) {
  const base = STATUS_TONES[tone] || STATUS_TONES.neutral;
  return (
    <div className={`${CARD} px-5 py-4 flex items-center gap-4`}>
      <span className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${base}1f`, color: TONE_INK[tone] || TONE_INK.neutral }} aria-hidden="true">
        {Icon && <Icon className="w-5 h-5" />}
      </span>
      <div className="min-w-0">
        <p className="text-sm text-slate-600">{label}</p>
        <p className="text-3xl font-semibold text-slate-900 tabular-nums leading-tight" style={HEADING_FONT}>{val}</p>
      </div>
    </div>
  );
}

export function PageHeading({ title, description, action }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <h1 className="text-3xl font-semibold text-slate-900 leading-tight" style={HEADING_FONT}>{title}</h1>
        <AccentRule className="mt-2" />
        {description && <p className="mt-3 text-sm text-slate-600 max-w-3xl">{description}</p>}
      </div>
      {action}
    </div>
  );
}

/** The short rule under a title. Navy by default; a department colour where one applies. */
export function AccentRule({ className = '', color = RULE_COLOR }) {
  return <span aria-hidden="true" className={`block h-1 w-10 rounded-full ${className}`} style={{ background: color }} />;
}

/** A section title: heading size, optional green count and hint, the rule under it. */
export function SectionTitle({ title, count, hint, aside, as: Tag = 'h2' }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <Tag className="text-xl font-semibold text-slate-900" style={HEADING_FONT}>{title}</Tag>
          {typeof count === 'number' && (
            <span className="text-xs font-medium tabular-nums px-2 py-0.5 rounded-full bg-green-50 text-[#1A7F37]">{count}</span>
          )}
          {hint && <span className="text-sm text-slate-500">{hint}</span>}
        </div>
        <AccentRule className="mt-2" />
      </div>
      {aside}
    </div>
  );
}

const TILE_SIZES = {
  sm: 'w-8 h-8 rounded-lg',
  md: 'w-10 h-10 rounded-lg',
  lg: 'w-12 h-12 rounded-xl',
};

/** A square icon badge in the class's department colour; dashed and grey when there's no class yet. */
export function ClassTile({ icon: Icon, color, size = 'md' }) {
  const tinted = color ? { background: `${color}1f`, color: classInk(color) } : { color: '#94a3b8' };
  return (
    <span
      className={`${TILE_SIZES[size] || TILE_SIZES.md} ${color ? '' : 'border border-dashed border-slate-300'} flex-shrink-0 inline-flex items-center justify-center`}
      style={tinted}
      aria-hidden="true"
    >
      {Icon && <Icon className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} />}
    </span>
  );
}

export function EmptyState({ icon: Icon, title, body, action, onClick, testId }) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`w-full ${CARD_FLAT} border-dashed p-10 text-center ${onClick ? 'hover:bg-slate-50 transition-colors' : ''}`}
      data-testid={testId}
    >
      {Icon && <Icon className="w-5 h-5 text-slate-400 mx-auto mb-3" />}
      <p className="text-sm font-medium text-slate-900">{title}</p>
      {body && <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">{body}</p>}
      {action && <p className="mt-4 text-sm font-medium text-[#1A7F37]">{action}</p>}
    </Tag>
  );
}
